import { CustomCode } from '@betterfunnel/types';
import Modal from '@betterfunnel/ui-core/Modal';
import Select from '@betterfunnel/ui-core/Select';
import TextField from '@betterfunnel/ui-core/TextField';
import Toggle from '@betterfunnel/ui-core/Toggle';
import { createCustomCode, validateCustomCode } from '@betterfunnel/utils';
import { useEffect, useState } from 'react';
import { CodeEditorTextArea } from '../FormFields/CodeEditor/CodeEditorTextArea';

interface CustomCodeEditorModalProps {
  customCode?: CustomCode;
  open: boolean;
  onClose: () => void;
  onSave: (code: CustomCode) => void;
  userId: string;
}

export default function CustomCodeEditorModal({
  customCode,
  open,
  onClose,
  onSave,
  userId,
}: CustomCodeEditorModalProps) {
  const isEditMode = !!customCode;

  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [location, setLocation] = useState<'head' | 'body'>('head');
  const [delay, setDelay] = useState<number>(0);
  const [enabled, setEnabled] = useState(true);
  const [errors, setErrors] = useState<string[]>([]);

  useEffect(() => {
    if (customCode) {
      setName(customCode.name);
      setCode(customCode.code);
      setLocation(customCode.location);
      setDelay(customCode.delay || 0);
      setEnabled(customCode.enabled);
    } else {
      // Reset for new code
      setName('');
      setCode('');
      setLocation('head');
      setDelay(0);
      setEnabled(true);
    }
    setErrors([]);
  }, [customCode, open]);

  const handleSave = () => {
    let codeToSave: CustomCode;

    if (isEditMode) {
      // Update existing code
      codeToSave = {
        ...customCode,
        name,
        code,
        location,
        delay: delay || undefined,
        enabled,
        updatedAt: new Date() as any,
      };
    } else {
      // Create new code
      codeToSave = createCustomCode(name, code, location, userId, {
        delay: delay || undefined,
        enabled,
      });
    }

    const validation = validateCustomCode(codeToSave);
    if (!validation.valid) {
      setErrors(validation.errors);
      return;
    }

    onSave(codeToSave);
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      actions={[
        {
          type: 'secondary',
          title: 'Cancel',
          onClick: onClose,
        },
        {
          type: 'primary',
          title: isEditMode ? 'Save Changes' : 'Add Code',
          onClick: handleSave,
        },
      ]}
    >
      <div className="p-6">
        <h2 className="mb-4 text-lg font-semibold text-gray-900">
          {isEditMode ? 'Edit Custom Code' : 'Add Custom Code'}
        </h2>

        <div className="space-y-4">
          {errors.length > 0 && (
            <div className="rounded-md bg-red-50 p-4">
              <div className="flex">
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">
                    Please fix the following errors:
                  </h3>
                  <div className="mt-2 text-sm text-red-700">
                    <ul className="list-disc space-y-1 pl-5">
                      {errors.map((error) => (
                        <li key={error}>{error}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          <TextField
            title="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., Google Analytics"
            input={{ required: true }}
          />

          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">
              Code <span className="text-red-500">*</span>
            </label>
            <CodeEditorTextArea
              value={code}
              onChange={setCode}
              className="h-56"
            />
          </div>

          <Select
            title="Location"
            selected={location}
            onChange={(value) => setLocation(value as 'head' | 'body')}
            options={[
              { value: 'head', child: 'Head' },
              { value: 'body', child: 'Body' },
            ]}
          />

          <TextField
            title="Delay (milliseconds)"
            type="number"
            value={delay.toString()}
            onChange={(e) => setDelay(parseInt(e.target.value) || 0)}
            placeholder="0"
            input={{ min: 0 }}
          />

          <Toggle label="Enabled" checked={enabled} onChange={setEnabled} />
        </div>
      </div>
    </Modal>
  );
}
